/**
 * convertMathNotation
 * Normalizes custom math input for remark-math/rehype-katex.
 *
 * Rules:
 *  - `\[ ... \]`  → `$$ ... $$`  (display math)
 *  - `\( ... \)`  → `$ ... $`    (inline math)
 *  - `[[ ... ]]`  → `$$ ... $$`  (explicit display math)
 *  - `$$ ... $$`  → preserved (already display math)
 *  - `$ ... $`    → preserved (already inline math)
 *  - Standalone math expressions are auto-detected and wrapped
 *  - Ignores content inside code fences and inline code
 *  - Uses Extract-Protect-Restore pattern to prevent orphan detection from matching inside existing math
 *
 * Notes:
 *  - Auto-detects mathematical expressions in user input
 *  - The AI backend should also wrap math with $ or $$
 *  - Use with: remarkMath({ singleDollarTextMath: false }) + rehypeKatex.
 */

// Valid LaTeX command suffixes for \cdot (used in malformed LaTeX cleanup)
// \cdots (horizontal ellipsis) and \cdotp (centered dot punctuation) are valid commands
const VALID_CDOT_SUFFIXES = ['s', 'p'] as const

// Helper: detect already-delimited math so we don't double-wrap
const hasMathDelims = (s: string) =>
  /\${1,2}[^$]+\${1,2}|\\\(|\\\)|\\\[|\\\]/.test(s)

// Helper: patterns that should NOT be wrapped as math (false positive prevention)
const isLikelyNotMath = (s: string): boolean => {
  const trimmed = s.trim()

  // Windows file paths (C:\Users, etc.)
  if (/^[A-Za-z]:\\/.test(trimmed)) return true
  if (/\\[A-Za-z]+\\/.test(trimmed)) return true

  // Individual Windows path segments (e.g., \Users, \Documents extracted from C:\Users\Documents)
  // The catchAllPattern captures these individually, so we need to match common folder names
  const windowsPathSegments = /^\\(Users|Documents|Program|Programs|Windows|System|System32|AppData|Desktop|Downloads|Pictures|Videos|Music|Public|ProgramData|Temp|Local|Roaming|Files|Data|Logs|Config|Backup|Scripts|Tools|Bin|Lib|Include|Share|Home|Root|Admin|Default|All|Common|Microsoft|Google|Apple|node_modules|src|dist|build|test|tests)$/i
  if (windowsPathSegments.test(trimmed)) return true

  // Version numbers (1.2.3, v2.0.1)
  if (/^v?\d+\.\d+(\.\d+)?$/.test(trimmed)) return true

  // IP addresses
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(trimmed)) return true

  return false
}

// LaTeX commands that are NOT math and should not be wrapped
const NON_MATH_COMMANDS = new Set([
  '\\section', '\\subsection', '\\chapter', '\\paragraph',
  '\\textbf', '\\textit', '\\emph', '\\underline',
  '\\textsubscript', '\\textsuperscript',  // Not supported by KaTeX, converted to Unicode in step 8.5
  '\\begin', '\\end', '\\item', '\\label', '\\ref',
  '\\newline', '\\linebreak', '\\pagebreak',
])

// Helper: fix prose accidentally inside math blocks
// The model sometimes fails to close math blocks before writing explanatory text
// This causes: ** → ∗∗ (asterisk operator), spaces ignored, prose merged
// Detection patterns: markdown **, conjunctions, colons with capitals
const fixProseInMathBlocks = (text: string, isDev: boolean): string => {
  // Patterns that indicate prose was placed inside math:
  // 1. ** (markdown bold) - should never appear in math
  // 2. Long lowercase sequences without operators (prose with ignored spaces)
  // 3. Conjunctions like ", but ", ", and ", "however"
  // 4. Colon followed by capital letter (explanatory text)

  // Process inline math ($...$) - NOT display math ($$...$$)
  // We need to be careful not to break display math or legitimate math content

  // First, protect display math blocks
  const displayMathTokens: string[] = []
  let processed = text.replace(/\$\$([\s\S]*?)\$\$/g, (match) => {
    displayMathTokens.push(match)
    return `__DISPLAY_MATH_${displayMathTokens.length - 1}__`
  })

  // Now process inline math blocks
  processed = processed.replace(/\$([^$]+)\$/g, (match, content: string) => {
    // Check for prose indicators
    const hasMarkdownBold = /\*\*/.test(content)
    const hasConjunction = /,\s*(but|and|or|however|therefore|thus|hence)\s/i.test(content)
    const hasExplanatoryColon = /:\s*[A-Z]/.test(content)
    const hasLongProseSequence = /[a-z]{15,}/.test(content) // Very long lowercase = merged words

    // Additional prose indicators:
    // Sentence-like text with common articles/verbs (not valid in math)
    const hasSentenceWords = /\b(the|a|an|is|are|was|were|has|have|this|that|these|those|with|for|from|into|within|between|using|where|when|if|then|also|only|all|each|every|some|any|no|not|can|will|would|should|must|may|might)\s+\w+/i.test(content)

    // Multiple consecutive words without math operators (likely prose, not math)
    const hasConsecutiveWords = /[a-z]+\s+[a-z]+\s+[a-z]+\s+[a-z]+/i.test(content) &&
                               !/[+\-*/=<>\\{}[\]^_]/.test(content)

    // Parenthetical prose like "(valid for Re > 10^5)"
    const hasParentheticalProse = /\((?:valid|where|when|if|for|with|using)\s+/i.test(content)

    if (!hasMarkdownBold && !hasConjunction && !hasExplanatoryColon &&
        !hasLongProseSequence && !hasSentenceWords && !hasConsecutiveWords &&
        !hasParentheticalProse) {
      return match // No issues detected, return as-is
    }

    if (isDev) {
      console.warn('[LaTeX] Detected prose in math block:', content.substring(0, 80))
    }

    // Strategy: Split before prose indicators
    // Find the point where prose starts and close the math block there

    // Case 1: Markdown bold ** in math - split before it
    if (hasMarkdownBold) {
      // Pattern: $math content - **Prose text**: more$
      // Should become: $math content$ - **Prose text**: more
      let didTransform = false
      const splitResult = content.replace(
        /^(.*?)(\s*-\s*|\s+)(\*\*.*)$/,
        (fullMatch, mathPart, separator, prosePart) => {
          if (mathPart.trim()) {
            didTransform = true
            return `$${mathPart.trim()}$${separator}${prosePart}`
          }
          // No math part - return original to avoid losing characters
          return fullMatch
        }
      )
      if (didTransform) {
        return splitResult
      }
    }

    // Case 2: Explanatory text after colon
    if (hasExplanatoryColon) {
      // Pattern: $equation: Explanation text$
      // Split at colon followed by capital letter
      const splitResult = content.replace(
        /^(.*?)(\s*:\s*)([A-Z].*)$/,
        (_, mathPart, colon, explanation) => {
          if (mathPart.trim()) {
            return `$${mathPart.trim()}$${colon}${explanation}`
          }
          return `${colon}${explanation}`
        }
      )
      if (splitResult !== content) {
        return splitResult
      }
    }

    // Case 3: Conjunction indicating prose continuation
    if (hasConjunction) {
      // Pattern: $equation, but this explanation$
      const splitResult = content.replace(
        /^(.*?)(,\s*)(but|and|or|however|therefore|thus|hence)(\s.*)$/i,
        (_, mathPart, comma, conj, rest) => {
          if (mathPart.trim()) {
            return `$${mathPart.trim()}$${comma}${conj}${rest}`
          }
          return `${comma}${conj}${rest}`
        }
      )
      if (splitResult !== content) {
        return splitResult
      }
    }

    // If we detected issues but couldn't fix them cleanly, return original
    // Better to show broken than to corrupt further
    return match
  })

  // Restore display math blocks
  processed = processed.replace(/__DISPLAY_MATH_(\d+)__/g, (_, i) => displayMathTokens[Number(i)])

  return processed
}

// Helper: fix common LaTeX malformations from model output
// Models sometimes merge LaTeX commands with following text (e.g., \cdotps instead of \cdot ps)
// Also fixes \bar used as pressure unit (not LaTeX overline command)
// NOTE: Backend _cleanup_latex_formatting handles some \cdot/\times cases but misses merged text
const fixMalformedLatex = (text: string, isDev: boolean): string => {
  // Valid LaTeX commands starting with \approx that should NOT be transformed
  const validApproxCommands = ['approxeq', 'approxcolon', 'approxcoloncolon']

  // Fix \approx merged with following text, but NOT valid commands like \approxeq
  // \approxkg → \approx\text{kg}, \approxm → \approx\text{m}, but \approxeq stays \approxeq
  let result = text.replace(/\\approx([a-z]+)/gi, (match, letters) => {
    // Check if this is a valid LaTeX command
    if (validApproxCommands.includes(`approx${letters.toLowerCase()}`)) {
      return match // Keep valid commands unchanged
    }
    if (isDev) {
      console.warn('[LaTeX] Fixed malformed \\approx:', match, '→', `\\approx\\text{${letters}}`)
    }
    return `\\approx\\text{${letters}}`
  })

  // Fix \bar used as pressure unit (not LaTeX \bar{x} command)
  // \bar followed by space/digit/punctuation = unit "bar", not the overline command
  // The LaTeX \bar command requires an argument like \bar{x}, so \bar alone is likely the unit
  result = result.replace(/\\bar(?![{a-zA-Z])/g, () => {
    if (isDev) {
      console.warn('[LaTeX] Fixed \\bar unit → bar')
    }
    return 'bar'
  })

  // Fix \cdot merged with following text (e.g., \cdotpS → \cdot pS)
  // Valid commands: \cdot, \cdots, \cdotp - don't transform these
  // Note: This is a fallback - backend LaTeXValidator should handle this first
  // IMPORTANT: LaTeX is case-sensitive - only lowercase \cdots and \cdotp are valid
  const validCdotSuffixes = ['s', 'p']
  result = result.replace(/\\cdot([a-zA-Z]+)/g, (match, letters) => {
    // Check if this is exactly 's' or 'p' (valid single-character suffixes)
    if (letters.length === 1 && validCdotSuffixes.includes(letters)) {
      return match // Valid: \cdots or \cdotp
    }
    // Check if it STARTS with 's' or 'p' followed by more chars (partial merge)
    else if (letters.length > 1 && validCdotSuffixes.includes(letters[0])) {
      // Partial merge: \cdotsome → \cdots ome or \cdotpx → \cdotp x
      const validCmd = letters[0] // 's' or 'p'
      const remaining = letters.substring(1)
      if (isDev) {
        console.warn('[LaTeX] Fixed partial merge:', match, '→', `\\cdot${validCmd} ${remaining}`)
      }
      return `\\cdot${validCmd} ${remaining}`
    }
    if (isDev) {
      console.warn('[LaTeX] Fixed merged \\cdot:', match, '→', `\\cdot ${letters}`)
    }
    return `\\cdot ${letters}` // Return LaTeX (consistent with backend)
  })

  // REMOVED: All LaTeX operator/syntax cleanup patterns moved to math block cleanup (lines 978-994)
  // These patterns should ONLY run inside math blocks where LaTeX commands exist
  // Running them on all text is unnecessary and could affect regular prose

  return result
}

// Helper: fix common missing backslashes in LaTeX commands
// AI models sometimes output "sqrt" instead of "\sqrt", "frac" instead of "\frac"
// This is a defensive fix that only applies in clear math contexts
const fixMissingBackslashes = (text: string, isDev: boolean): string => {
  // Common math commands that AI often outputs without backslash
  // Only include commands that are NEVER valid English words
  const mathCommands = [
    'sqrt', 'frac', 'dfrac', 'tfrac',
    'sum', 'prod', 'int', 'oint', 'iint', 'iiint',
    'lim', 'limsup', 'liminf',
    'partial', 'nabla', 'infty',
    'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'zeta', 'eta', 'theta',
    'iota', 'kappa', 'lambda', 'mu', 'nu', 'xi', 'omicron', 'pi', 'rho',
    'sigma', 'tau', 'upsilon', 'phi', 'chi', 'psi', 'omega',
    'Delta', 'Gamma', 'Lambda', 'Phi', 'Pi', 'Psi', 'Sigma', 'Theta', 'Omega',
    'cdot', 'cdots', 'ldots', 'vdots', 'ddots',
    'leq', 'geq', 'neq', 'approx', 'equiv', 'sim', 'simeq', 'propto',
    'leftarrow', 'rightarrow', 'Leftarrow', 'Rightarrow', 'leftrightarrow',
    'mathrm', 'mathbf', 'mathit', 'mathsf', 'mathtt', 'mathcal', 'mathbb',
    'textbf', 'textit', 'textrm', 'textsf',
    'overline', 'underline', 'overbrace', 'underbrace',
    'vec', 'hat', 'bar', 'dot', 'ddot', 'tilde'
  ]

  let result = text

  for (const cmd of mathCommands) {
    // Pattern: command word that is:
    // 1. NOT preceded by backslash (already escaped)
    // 2. NOT part of a larger word (word boundary)
    // 3. Followed by { or space+{ or digit (math context indicator)
    const pattern = new RegExp(
      `(?<!\\\\)(?<!\\w)(${cmd})(?=\\s*[{\\d]|\\s*\\()`,
      'g'
    )
    result = result.replace(pattern, (match) => {
      if (isDev) {
        console.warn(`[LaTeX] Fixed missing backslash: ${match} → \\${match}`)
      }
      return `\\${match}`
    })
  }

  return result
}

// Helper: fix orphan \) or \( that aren't part of delimiter pairs
// AI sometimes outputs \) when it means ) (typo)
// Uses depth-tracking to detect orphans even when global counts are equal
const fixOrphanDelimiters = (text: string, isDev: boolean): string => {
  let result = ''
  let depth = 0
  let i = 0

  // First pass: fix orphan \) (closing without opening)
  while (i < text.length) {
    // Check for \( or \)
    if (i < text.length - 1 && text[i] === '\\') {
      if (text[i + 1] === '(') {
        // Opening delimiter - increase depth
        depth++
        result += '\\('
        i += 2
        continue
      } else if (text[i + 1] === ')') {
        if (depth > 0) {
          // Valid closing - has matching open
          depth--
          result += '\\)'
        } else {
          // Orphan \) - no matching \(, convert to plain )
          result += ')'
          if (isDev) {
            console.warn('[LaTeX] Fixed orphan \\) → )')
          }
        }
        i += 2
        continue
      }
    }
    result += text[i]
    i++
  }

  // If depth > 0, we have unclosed \( at end
  // Need a second pass to fix them by converting to plain (
  if (depth > 0) {
    if (isDev) {
      console.warn(`[LaTeX] ${depth} unclosed \\( delimiters - fixing...`)
    }

    // Second pass: work backwards to find and fix unclosed \(
    let finalResult = ''

    // Scan forward again, but this time track which \( are unclosed
    const openPositions: number[] = []
    for (let j = 0; j < result.length - 1; j++) {
      if (result[j] === '\\') {
        if (result[j + 1] === '(') {
          openPositions.push(j)
        } else if (result[j + 1] === ')') {
          if (openPositions.length > 0) {
            openPositions.pop()  // This \) closes the most recent \(
          }
        }
      }
    }

    // openPositions now contains positions of unclosed \(
    // Convert them to plain (
    const unclosedSet = new Set(openPositions)
    for (let j = 0; j < result.length; j++) {
      if (unclosedSet.has(j) && j < result.length - 1 && result[j] === '\\' && result[j + 1] === '(') {
        finalResult += '('
        j++  // Skip the ( we just processed
        if (isDev) {
          console.warn('[LaTeX] Fixed unclosed \\( → (')
        }
      } else {
        finalResult += result[j]
      }
    }

    return finalResult
  }

  return result
}

// Helper: ensure blank lines after tables to prevent prose being absorbed
// GFM tables continue until a blank line is encountered
// If AI doesn't output blank line after table, following text gets parsed as part of table
const ensureBlankLinesAfterTables = (text: string, isDev: boolean): string => {
  const lines = text.split('\n')
  const result: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const nextLine = lines[i + 1]

    result.push(line)

    // If current line is a table row (starts with | and has at least one more |)
    if (line.trim().startsWith('|') && line.indexOf('|', 1) !== -1) {
      // And next line exists and is NOT a table row and NOT empty
      if (nextLine !== undefined && !nextLine.trim().startsWith('|') && nextLine.trim() !== '') {
        // Insert blank line to properly terminate the table
        result.push('')
        if (isDev) {
          console.warn('[Table] Inserted blank line after table before:', nextLine.substring(0, 50))
        }
      }
    }
  }

  return result.join('\n')
}

// Helper: convert HTML subscripts/superscripts to Unicode
// react-markdown doesn't process raw HTML without rehype-raw plugin
// AI sometimes outputs <sub>, <sup>, <br> instead of LaTeX
const convertHtmlToPlainText = (text: string, isDev: boolean): string => {
  let result = text

  // Convert <sub>...</sub> to subscript Unicode where possible
  const subscriptMap: Record<string, string> = {
    '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
    '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
    'a': 'ₐ', 'e': 'ₑ', 'h': 'ₕ', 'i': 'ᵢ', 'j': 'ⱼ',
    'k': 'ₖ', 'l': 'ₗ', 'm': 'ₘ', 'n': 'ₙ', 'o': 'ₒ',
    'p': 'ₚ', 'r': 'ᵣ', 's': 'ₛ', 't': 'ₜ', 'u': 'ᵤ',
    'v': 'ᵥ', 'x': 'ₓ'
  }

  const superscriptMap: Record<string, string> = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
    '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾',
    'n': 'ⁿ', 'i': 'ⁱ'
  }

  // Convert <sub>content</sub>
  result = result.replace(/<sub>([^<]+)<\/sub>/gi, (match, content: string) => {
    const converted = content.split('').map((c: string) => subscriptMap[c] || c).join('')
    if (converted !== content && isDev) {
      console.warn(`[HTML] Converted subscript: ${match} → ${converted}`)
    }
    // If fully converted, use Unicode; otherwise use underscore notation
    return converted !== content ? converted : `_${content}`
  })

  // Convert <sup>content</sup>
  result = result.replace(/<sup>([^<]+)<\/sup>/gi, (match, content: string) => {
    const converted = content.split('').map((c: string) => superscriptMap[c] || c).join('')
    if (converted !== content && isDev) {
      console.warn(`[HTML] Converted superscript: ${match} → ${converted}`)
    }
    // If fully converted, use Unicode; otherwise use caret notation
    return converted !== content ? converted : `^${content}`
  })

  // Convert <br> and <br/> to newlines
  result = result.replace(/<br\s*\/?>/gi, '\n')

  return result
}

// Helper: convert inline math in tables to Unicode symbols
// remarkGfm processes tables BEFORE remarkMath, so $...$ in table cells doesn't render
// Convert Greek letters and simple math to Unicode equivalents
const convertMathInTablesToUnicode = (text: string, isDev: boolean): string => {
  // Greek letter map
  const greekMap: Record<string, string> = {
    'alpha': 'α', 'beta': 'β', 'gamma': 'γ', 'delta': 'δ', 'epsilon': 'ε',
    'zeta': 'ζ', 'eta': 'η', 'theta': 'θ', 'iota': 'ι', 'kappa': 'κ',
    'lambda': 'λ', 'mu': 'μ', 'nu': 'ν', 'xi': 'ξ', 'omicron': 'ο',
    'pi': 'π', 'rho': 'ρ', 'sigma': 'σ', 'tau': 'τ', 'upsilon': 'υ',
    'phi': 'φ', 'chi': 'χ', 'psi': 'ψ', 'omega': 'ω',
    'Delta': 'Δ', 'Gamma': 'Γ', 'Lambda': 'Λ', 'Phi': 'Φ', 'Pi': 'Π',
    'Psi': 'Ψ', 'Sigma': 'Σ', 'Theta': 'Θ', 'Omega': 'Ω'
  }

  return text.split('\n').map(line => {
    if (!line.trim().startsWith('|')) return line  // Not a table row

    let cleaned = line

    // Convert $\greek$ to Unicode symbol (e.g., $\rho$ → ρ)
    cleaned = cleaned.replace(/\$\\([a-zA-Z]+)\$/g, (match, cmd: string) => {
      const symbol = greekMap[cmd]
      if (symbol) {
        if (isDev) console.warn(`[LaTeX] Table math → Unicode: ${match} → ${symbol}`)
        return symbol
      }
      return match  // Unknown command, keep as-is for now
    })

    // Remove remaining $ delimiters from simple math that won't render anyway
    // This catches $x$ → x, $\rho$ → \rho (if not converted above)
    // Only apply to simple content (no nested braces or complex commands)
    cleaned = cleaned.replace(/\$([^$|{}\\]+)\$/g, '$1')

    return cleaned
  }).join('\n')
}

// Helper: clean up malformed math delimiters in markdown tables
// remarkGfm processes tables before remarkMath, so $$ in cells becomes literal
// Strategy: Be aggressive - remove ALL orphan $$ from table rows since display math doesn't render in tables anyway
const cleanMathInTables = (text: string, isDev: boolean): string => {
  return text.split('\n').map(line => {
    if (!line.trim().startsWith('|')) {
      return line  // Not a table row
    }

    let cleaned = line

    // Count $$ in the line
    const dollarPairs = (line.match(/\$\$/g) || []).length

    if (dollarPairs % 2 !== 0) {
      // Odd number = orphan(s), remove all $$ from this table row
      cleaned = line.replace(/\$\$/g, '')
      if (isDev) {
        console.warn('[LaTeX] Removed orphan $$ from table row (odd count)')
      }
    } else if (dollarPairs > 0) {
      // Even number but check if they're actually paired or separate orphans
      // In tables, display math ($$...$$) doesn't render anyway

      // Remove empty $$$$ (adjacent pairs with no content)
      cleaned = cleaned.replace(/\$\$\s*\$\$/g, '')

      // Remove | $$ | (standalone in cell)
      cleaned = cleaned.replace(/\|\s*\$\$\s*\|/g, '| |')

      // Remove $$ at start of cell with space before content: | $$ content |
      cleaned = cleaned.replace(/\|\s*\$\$\s+([^|]+)\|/g, '| $1|')

      // Remove $$ at end of cell with space after content: | content $$ |
      cleaned = cleaned.replace(/\|\s*([^|]+)\s+\$\$\s*\|/g, '| $1 |')

      if (cleaned !== line && isDev) {
        console.warn('[LaTeX] Cleaned $$ from table row')
      }
    }

    return cleaned
  }).join('\n')
}

// Helper: balance unclosed math delimiters to prevent cascade failures
// If model outputs $$ without closing, everything after breaks
const balanceDelimiters = (text: string, isDev: boolean): string => {
  // Step 1: Balance $$ (display math) first
  const displayMatches = text.match(/\$\$/g) || []
  if (displayMatches.length % 2 !== 0) {
    // Odd number of $$ - add closing $$ at end
    // NOTE: Don't add newline - it breaks markdown table structure
    text = text + '$$'
    if (isDev) {
      console.warn('[LaTeX] Auto-closed unclosed $$ delimiter')
    }
  }

  // Step 2: Count single $ only OUTSIDE of $$...$$ blocks
  // After splitting by $$, segments at even indexes (0, 2, 4...) are outside display math
  // Segments at odd indexes (1, 3, 5...) are inside display math (content, not delimiters)
  const segments = text.split(/\$\$/)
  let singleDollarCount = 0

  for (let i = 0; i < segments.length; i++) {
    // Only count $ in even-indexed segments (outside display math)
    if (i % 2 === 0) {
      const matches = segments[i].match(/\$/g) || []
      singleDollarCount += matches.length
    }
  }

  if (singleDollarCount % 2 !== 0) {
    // Odd number of single $ outside display math - add closing $ at end
    text = text + '$'
    if (isDev) {
      console.warn('[LaTeX] Auto-closed unclosed $ delimiter')
    }
  }

  return text
}

// Helper: check if this looks like a mathematical expression
const isMathExpression = (s: string) => {
  const trimmed = s.trim()

  // Skip if too long (likely prose)
  if (trimmed.length > 200) return false

  // Has LaTeX commands like \frac, \sum, etc.
  if (/\\[a-zA-Z]+/.test(trimmed)) return true

  // Has special math symbols (excluding → which is handled separately for chemical equations)
  if (/[∑∏∫∂∇±∓≈≠≤≥⊂⊃⊆⊇∈∉∪∩×÷⇒⇔∀∃]/.test(trimmed)) return true

  // Chemical equation pattern (e.g., N2 + O2 → N2O5)
  // Only if it's a standalone equation, not part of a sentence
  if (
    /^[A-Z][a-z]?\d*(?:\s*[+]\s*[A-Z][a-z]?\d*)*\s*→\s*[A-Z][a-z]?\d*(?:\s*[+]\s*[A-Z][a-z]?\d*)*$/.test(
      trimmed
    )
  )
    return true

  // Mathematical expression pattern (numbers with operators)
  // Must have at least one operator and look like math
  if (
    /^[\d\s+\-*/^().=]+$/.test(trimmed) &&
    /[+\-*/^=]/.test(trimmed)
  ) {
    // But not simple things like "1+1" without context
    return (
      trimmed.length > 3 &&
      (/[*/^]/.test(trimmed) ||
        /\d+\s*[+-]\s*\d+\s*[+\-*/^]/.test(trimmed))
    )
  }

  return false
}

export const convertMathNotation = (raw: string): string => {
  if (typeof raw !== 'string' || raw.length === 0) return ''
  const isDev = import.meta.env.DEV

  
  // DEBUG: Only log when input contains problematic patterns
  const shouldDebug = isDev && (raw.includes('\\textsubscript') || raw.includes('\\cdot'))
  if (shouldDebug) {
    console.group('🔍 convertMathNotation DEBUG')
    console.log('Input (first 300 chars):', raw.substring(0, 300))
  }

  try {
    // 0) Shield code blocks/spans so we don't alter them
  const codeTokens: string[] = []
  const protect = (s: string) =>
    s.replace(/(```[\s\S]*?```|`[^`\n]*`)/g, (m) => {
      codeTokens.push(m)
      return `__CODE_TOKEN_${codeTokens.length - 1}__`
    })
  const restoreCode = (s: string) =>
    s.replace(/__CODE_TOKEN_(\d+)__/g, (_, i) => codeTokens[Number(i)])

  let text = protect(raw)

  // 0.05) Ensure blank lines after tables to prevent prose being absorbed
  // GFM tables continue until blank line - if AI doesn't output one, text gets absorbed
  // Must run EARLY before any math processing
  text = ensureBlankLinesAfterTables(text, isDev)

  // 0.08) Convert HTML subscripts/superscripts to Unicode
  // react-markdown doesn't process raw HTML; AI sometimes outputs <sub>/<sup>/<br>
  text = convertHtmlToPlainText(text, isDev)

  // 0.1) Fix prose accidentally placed inside math blocks
  // This must run FIRST before any other processing to catch model errors
  // e.g., $d = 26.2mm - **Discharge coefficient**: 0.61$ → $d = 26.2mm$ - **Discharge coefficient**: 0.61
  text = fixProseInMathBlocks(text, isDev)

  // 0.25) Fix common LaTeX malformations from model output
  // e.g., \approxkg → \approx\text{kg}
  // Note: \cdot and \times are handled by backend _cleanup_latex_formatting
  text = fixMalformedLatex(text, isDev)

  // 0.3) Fix missing backslashes on common math commands
  // e.g., "sqrt{x}" → "\sqrt{x}", "frac{a}{b}" → "\frac{a}{b}"
  text = fixMissingBackslashes(text, isDev)

  // 0.5) Balance unclosed delimiters to prevent cascade failures
  // Must run BEFORE delimiter conversion so we count the original delimiters
  text = balanceDelimiters(text, isDev)

  // 0.55) Convert inline math in tables to Unicode symbols
  // remarkGfm processes tables BEFORE remarkMath, so $...$ in table cells won't render
  // Convert Greek letters ($\rho$ → ρ) and strip remaining $ delimiters
  text = convertMathInTablesToUnicode(text, isDev)

  // 0.6) Clean malformed math delimiters in markdown tables
  // Must run AFTER balanceDelimiters to catch any $$ it adds
  // remarkGfm processes tables before remarkMath, so orphan $$ in cells becomes literal text
  text = cleanMathInTables(text, isDev)

  // 0.9) Fix orphan \) or \( that AI outputs as typos (e.g., \) instead of ))
  // Must run BEFORE converting \(...\) to $...$
  text = fixOrphanDelimiters(text, isDev)

  // 1) First, normalize backslash-style delimiters to dollar signs
  //    \[ ... \] -> $$ ... $$
  text = text.replace(/\\\[([\s\S]*?)\\\]/g, (_m, body) => `$$${body}$$`)
  //    \( ... \) -> $ ... $
  text = text.replace(/\\\(([\s\S]*?)\\\)/g, (_m, body) => `$${body}$`)

  // 2) Handle double brackets [[ ... ]] for explicit display math
  text = text.replace(/\[\[([\s\S]*?)\]\]/g, (_m, body) => {
    // Don't convert if already has math delimiters
    if (/\$/.test(body)) return _m
    return `$$${body}$$`
  })

  /**
   * ============================================================================
   * TWO-PHASE TEXTSUBSCRIPT/TEXTSUPERSCRIPT PROCESSING
   * ============================================================================
   *
   * Why we need two phases:
   *
   * PHASE 1 (Early - Step 2.5, BEFORE math protection):
   * - Catches AI-generated invalid LaTeX like $_{\textsubscript{x}}$
   * - \textsubscript is NOT valid inside math mode
   * - If we don't catch it early, it gets protected with math blocks
   * - Uses simple regex with iterative nesting for performance
   *
   * PHASE 2 (Late - Step 8.5, AFTER math restoration):
   * - Processes remaining \textsubscript/\textsuperscript commands
   * - Handles edge cases with protected content
   * - Uses depth-counting brace extraction for robustness
   *
   * Double-processing prevention:
   * - Early phase converts commands to Unicode or fallback prefix
   * - Late phase only matches \textsubscript pattern (not present after conversion)
   * - Placeholder checks prevent corruption of protected content
   *
   * Example flow:
   * Input:  "Text $x$ and \textsubscript{\textit{a}}"
   * Step 2.5: "Text $x$ and ₐ" (early converts subscript)
   * Step 3:   "Text __MATH_INLINE_0__ and ₐ" (math protected)
   * Step 7:   "Text __MATH_INLINE_0__ and ₐ" (late finds no \textsubscript)
   * Step 11:  "Text $x$ and ₐ" (math restored)
   * ============================================================================
   */

  // 2.5) Pre-process textsubscript/textsuperscript BEFORE protecting math blocks
  // AI sometimes generates invalid LaTeX like $_{\textsubscript{x}}$ which needs fixing
  // \textsubscript is NOT valid inside math mode - convert before KaTeX sees it

  /**
   * UNICODE SUBSCRIPT/SUPERSCRIPT CHARACTER MAPS
   *
   * These maps contain characters that have Unicode equivalents.
   * Characters NOT in these maps will use fallback notation:
   * - Subscript: _x (underscore prefix)
   * - Superscript: ^x (caret prefix)
   *
   * Intentionally limited scope:
   * - Only digits and select lowercase letters for subscripts
   * - Only digits, select operators, and letters n, i for superscripts
   * - No uppercase letters (no Unicode equivalents exist)
   * - No special symbols (most don't have Unicode equivalents)
   *
   * Examples:
   * - \textsubscript{a} → ₐ (in map)
   * - \textsubscript{b} → _b (not in map, uses fallback)
   * - \textsuperscript{2} → ² (in map)
   * - \textsuperscript{th} → ^th (not all chars in map, uses fallback)
   *
   * Note: These maps are shared between early (step 2.5) and late (step 8.5) processing
   */
  const SUBSCRIPT_MAP: Record<string, string> = {
    '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
    '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
    'a': 'ₐ', 'e': 'ₑ', 'h': 'ₕ', 'i': 'ᵢ', 'j': 'ⱼ',
    'k': 'ₖ', 'l': 'ₗ', 'm': 'ₘ', 'n': 'ₙ', 'o': 'ₒ',
    'p': 'ₚ', 'r': 'ᵣ', 's': 'ₛ', 't': 'ₜ', 'u': 'ᵤ',
    'v': 'ᵥ', 'x': 'ₓ'
  }

  const SUPERSCRIPT_MAP: Record<string, string> = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
    '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾',
    'n': 'ⁿ', 'i': 'ⁱ'
  }

  /**
   * Helper: Strip text formatting commands to get plain text
   *
   * Removes \textbf, \textit, \texttt, \emph, \mbox commands while preserving content.
   * Uses iterative approach to handle arbitrary nesting depth.
   *
   * IMPORTANT: Only use within \textsubscript/\textsuperscript processing.
   * Do NOT strip these commands globally as they are valid KaTeX commands.
   *
   * Example: \textit{\textbf{complex}} → complex
   *
   * @param text - Text containing formatting commands
   * @returns Text with formatting commands removed
   */
  const stripTextFormatting = (text: string): string => {
    let result = text
    let changed = true
    while (changed) {
      const before = result
      // Match commands with content that has no nested braces (processes innermost first)
      result = result.replace(/\\(?:textbf|textit|texttt|emph|mbox)\{([^{}]*)\}/g, '$1')
      changed = before !== result
    }
    return result
  }

  // Iterative processing for textsubscript/textsuperscript (before defining helper functions)
  // This catches AI-generated invalid LaTeX like $_{\textsubscript{complex}}$
  // Use iterative approach to handle arbitrary nesting depth
  let changed = true
  let iterations = 0
  const MAX_ITERATIONS = 100
  while (changed && iterations < MAX_ITERATIONS) {
    const before = text
    iterations++

    text = text.replace(/\\textsubscript\{([^{}]*)\}/g, (_match, content) => {
      // CRITICAL: Check for placeholder tokens to prevent corruption
      // Match any placeholder format: __PREFIX_NUMBER__
      if (/__\w+_\d+__/.test(content)) {
        if (isDev && shouldDebug) {
          console.log(`  [Early] \\textsubscript{${content}} - skipped (contains placeholder)`)
        }
        return `_${content}`
      }

      // Strip formatting commands from content first
      const stripped = content.includes('\\') ? stripTextFormatting(content) : content

      // CRITICAL: Check if stripped version still has backslashes (non-formatting LaTeX)
      if (stripped.includes('\\')) {
        if (isDev && shouldDebug) {
          console.log(`  [Early] \\textsubscript{${content}} - contains non-formatting LaTeX, using fallback`)
        }
        return `_${content}`
      }

      // Convert to Unicode subscript (content has no braces at this point)
      const converted = stripped.split('').map((c: string) => SUBSCRIPT_MAP[c] || c).join('')
      // Use fallback prefix if no conversion occurred
      const finalResult = converted !== stripped ? converted : `_${stripped}`
      if (isDev && shouldDebug) {
        console.log(`  [Early] \\textsubscript{${content}} → ${finalResult}`)
      }
      return finalResult
    })

    text = text.replace(/\\textsuperscript\{([^{}]*)\}/g, (_match, content) => {
      // CRITICAL: Check for placeholder tokens to prevent corruption
      // Match any placeholder format: __PREFIX_NUMBER__
      if (/__\w+_\d+__/.test(content)) {
        if (isDev && shouldDebug) {
          console.log(`  [Early] \\textsuperscript{${content}} - skipped (contains placeholder)`)
        }
        return `^${content}`
      }

      // Strip formatting commands from content first
      const stripped = content.includes('\\') ? stripTextFormatting(content) : content

      // CRITICAL: Check if stripped version still has backslashes (non-formatting LaTeX)
      if (stripped.includes('\\')) {
        if (isDev && shouldDebug) {
          console.log(`  [Early] \\textsuperscript{${content}} - contains non-formatting LaTeX, using fallback`)
        }
        return `^${content}`
      }

      // Convert to Unicode superscript (content has no braces at this point)
      const converted = stripped.split('').map((c: string) => SUPERSCRIPT_MAP[c] || c).join('')
      // Use fallback prefix if no conversion occurred
      const finalResult = converted !== stripped ? converted : `^${stripped}`
      if (isDev && shouldDebug) {
        console.log(`  [Early] \\textsuperscript{${content}} → ${finalResult}`)
      }
      return finalResult
    })

    changed = before !== text
  }

  // Check if we hit the max iterations limit
  if (iterations >= MAX_ITERATIONS) {
    // Log warning in all environments (production needs visibility too)
    console.warn('[LaTeX] Early processing hit max iterations - returning original to avoid corruption')

    // In development, provide additional diagnostic info
    if (isDev) {
      console.warn('[LaTeX] Text snippet that caused issue (first 200 chars):', text.substring(0, 200))
      console.warn('[LaTeX] Remaining \\textsubscript or \\textsuperscript commands:',
        (text.match(/\\text(?:sub|super)script\{/g) || []).length)
    }

    // Return original input to avoid corrupting output
    // Better to show raw LaTeX than garbled half-processed text
    return raw
  }

  // 3) EXTRACT AND PROTECT existing math blocks before orphan detection
  // This prevents orphan detection from matching commands inside existing delimiters
  const mathTokens: string[] = []
  const protectMath = (s: string) => {
    // Protect display math blocks ($$...$$) first
    let result = s.replace(/\$\$([\s\S]*?)\$\$/g, (m) => {
      mathTokens.push(m)
      return `__MATH_DISPLAY_${mathTokens.length - 1}__`
    })
    // Then protect inline math ($...$)
    result = result.replace(/\$([^$\n]+?)\$/g, (m) => {
      mathTokens.push(m)
      return `__MATH_INLINE_${mathTokens.length - 1}__`
    })
    return result
  }
  const restoreMath = (s: string) =>
    s.replace(/__MATH_(DISPLAY|INLINE)_(\d+)__/g, (_, _type, i) => mathTokens[Number(i)])

  // Extract existing math blocks
  text = protectMath(text)

  // 4) Fix nested delimiters and malformed commands in the extracted math blocks
  mathTokens.forEach((block, index) => {
    let fixed = block

    // Fix nested $$ in aligned blocks: $$\begin{aligned}$$ $$eq$$ $$\end{aligned}$$
    if (fixed.includes('\\begin{aligned}')) {
      fixed = fixed.replace(/\$\$\\begin\{aligned\}\$\$([\s\S]*?)\$\$\\end\{aligned\}\$\$/g, (_match, inner) => {
        const cleaned = inner.replace(/\$\$/g, ' ').replace(/\$/g, '')
        return `$$\\begin{aligned}${cleaned}\\end{aligned}$$`
      })
    }

    // Remove nested $ inside $$ blocks
    if (fixed.startsWith('$$')) {
      const inner = fixed.slice(2, -2)
      if (inner.includes('$')) {
        const cleaned = inner.replace(/\$/g, '')
        fixed = `$$${cleaned}$$`
        if (isDev) {
          console.warn('[LaTeX] Cleaned nested $ in block:', block.substring(0, 50))
        }
      }
    }

    // Fix \cdot merged with following text inside math blocks (e.g., \cdotpA → \cdotp A)
    // Valid commands: \cdots, \cdotp - don't transform these
    // This is a frontend fallback for backend fix (backend should handle this, but may not be deployed)
    // IMPORTANT: LaTeX is case-sensitive - only lowercase \cdots and \cdotp are valid
    fixed = fixed.replace(/\\cdot([a-zA-Z]+)/g, (match, letters) => {
      // Check if this is exactly 's' or 'p' (valid single-character suffixes)
      if (letters.length === 1 && VALID_CDOT_SUFFIXES.includes(letters as 's' | 'p')) {
        return match // Valid: \cdots or \cdotp
      }
      // Check if it STARTS with 's' or 'p' followed by more chars (partial merge)
      else if (letters.length > 1 && VALID_CDOT_SUFFIXES.includes(letters[0] as 's' | 'p')) {
        // Partial merge: \cdotsome → \cdots ome or \cdotpx → \cdotp x
        const validCmd = letters[0] // 's' or 'p'
        const remaining = letters.substring(1)
        if (isDev) {
          console.warn(`[LaTeX] Fixed partial merge in math block: ${match} → \\cdot${validCmd} ${remaining}`)
        }
        return `\\cdot${validCmd} ${remaining}`
      }
      // Insert space to separate \cdot from following text
      if (isDev) {
        console.warn(`[LaTeX] Fixed merged \\cdot in math block: ${match} → \\cdot ${letters}`)
      }
      return `\\cdot ${letters}`
    })

    // Fix malformed operators in math expressions (AI sometimes generates invalid syntax)
    // IMPORTANT: Use negative lookbehind to preserve valid \; spacing commands
    fixed = fixed.replace(/(?<!\\);(\\[a-z]+)(?<!\\);/gi, ' $1 ')  // ;\approx; → \approx (but not \;\approx\;)
    fixed = fixed.replace(/(?<!\\);=(?<!\\);/g, '=')  // ;=; → = (but not \;=\;)
    fixed = fixed.replace(/(?<!\\);=/g, '=')          // ;= → = (but not \;=)
    fixed = fixed.replace(/=(?<!\\);/g, '=')          // =; → = (but not =\;)
    fixed = fixed.replace(/(?<!\\);(?<!\\);/g, '')    // ;; → (empty) (but not \;\;)

    // Fix spurious semicolons in various contexts
    // IMPORTANT: Also use negative lookbehind to preserve valid \; spacing
    fixed = fixed.replace(/(?<!\\)([}0-9]);(?![=])/g, '$1 ')  // }; or 3; → } or 3 (but not \; or ;=)
    fixed = fixed.replace(/(?<!\\)([a-zA-Z]);(?=\[)/g, '$1')  // r;[ → r[ (but not \;[)

    // Fix exclamation marks after LaTeX commands
    fixed = fixed.replace(/(\\[a-z]+)!/g, '$1')  // \sin!, \cos! → \sin, \cos

    // Fix commas between \text blocks (e.g., \text{m},\text{s} → \text{m} \cdot \text{s})
    fixed = fixed.replace(/\\text\{([^}]+)\},\\text\{/g, '\\text{$1} \\cdot \\text{')

    // Fix commas with spaces in the middle of math expressions
    // CONSERVATIVE: Only fix " , " pattern (malformed, not in valid LaTeX)
    fixed = fixed.replace(/([a-zA-Z}]) , /g, '$1 ')  // V , I , → V I

    // REMOVED aggressive comma patterns - they break f(a,b), (x,y), \max(a,b,c)

    mathTokens[index] = fixed
  })

  // 5) NOW detect orphan LaTeX commands (only in non-math text)
  // This runs on text where existing math is replaced with placeholders
  // Improved patterns to handle nested braces and complex commands

  // Pattern 1: Commands with nested braces (like \frac{T_{\text{act}}}{T_{\text{std}}})
  // Allows one level of nesting: \{(?:[^{}]|\{[^}]*\})*\}
  const complexOrphanPattern = /(?<![\\$])(\\frac\{(?:[^{}]|\{[^}]*\})*\}\{(?:[^{}]|\{[^}]*\})*\}|\\sqrt\{(?:[^{}]|\{[^}]*\})*\}|\\text\{(?:[^{}]|\{[^}]*\})*\}|\\mathrm\{(?:[^{}]|\{[^}]*\})*\}|\\mathbf\{(?:[^{}]|\{[^}]*\})*\}|\\boxed\{(?:[^{}]|\{[^}]*\})*\})/g

  // Pattern 2: \left and \right pairs
  const leftRightPattern = /(?<![\\$])(\\left[([{](?:[^\\]|\\(?!right))*\\right[)\]}])/g

  // Pattern 3: Simple commands with single argument (supports one level of nested braces)
  const simpleOrphanPattern = /(?<![\\$])(\\(?:sum|int|prod|lim|partial|nabla|infty)\{(?:[^{}]|\{[^}]*\})*\})/g

  // Pattern 4: Commands without arguments (comprehensive list to prevent similar rendering issues)
  // Selection criteria: Commands that frequently appear standalone in mathematical text and are
  // supported by KaTeX v0.16.22. Includes: basic operators (times, cdot, cdotp, div), comparison
  // operators (approx, leq, geq), set theory (subset, in, emptyset), logic (forall, exists),
  // arrows (rightarrow, Rightarrow), Greek letters (alpha, beta, gamma), and special symbols
  // (infty, partial, nabla). This prevents raw LaTeX from appearing in rendered output.
  // Note: infty, partial, nabla included here for standalone usage (without braces)
  // They're also in simpleOrphanPattern for when braces ARE present (e.g., \infty{x})
  const operatorPattern = /(?<![\\$])(\\(?:times|cdot|cdotp|cdots|ldots|vdots|ddots|pm|mp|div|ast|star|bigstar|circ|bullet|bigcirc|oplus|ominus|otimes|odot|oslash|boxplus|boxminus|boxtimes|boxdot|approx|equiv|neq|ne|leq|le|geq|ge|ll|gg|lll|ggg|prec|succ|preceq|succeq|sim|simeq|cong|asymp|doteq|propto|parallel|perp|mid|nmid|subset|subseteq|supset|supseteq|subsetneq|supsetneq|sqsubset|sqsupset|sqsubseteq|sqsupseteq|in|notin|ni|notni|cap|cup|sqcap|sqcup|wedge|vee|barwedge|veebar|emptyset|varnothing|exists|nexists|forall|neg|lnot|land|lor|implies|iff|to|gets|mapsto|longmapsto|rightarrow|leftarrow|Rightarrow|Leftarrow|leftrightarrow|Leftrightarrow|longrightarrow|longleftarrow|Longrightarrow|Longleftarrow|longleftrightarrow|Longleftrightarrow|uparrow|downarrow|updownarrow|Uparrow|Downarrow|Updownarrow|nearrow|searrow|swarrow|nwarrow|rightharpoonup|rightharpoondown|leftharpoonup|leftharpoondown|angle|measuredangle|sphericalangle|triangle|triangledown|triangleleft|triangleright|square|blacksquare|lozenge|diamond|clubsuit|spadesuit|heartsuit|diamondsuit|sharp|flat|natural|checkmark|cross|maltese|infty|partial|nabla|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|vartheta|iota|kappa|varkappa|lambda|mu|nu|xi|omicron|pi|varpi|rho|varrho|sigma|varsigma|tau|upsilon|phi|varphi|chi|psi|omega|digamma|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Upsilon|Phi|Psi|Omega|aleph|alef|beth|gimel|daleth|eth|hbar|hslash|imath|jmath|ell|wp|mho|Re|Im|top|bot|dag|ddag|S|P|copyright|textregistered|yen|euro|pounds|degree|prime|backprime|complement|surd)(?![a-zA-Z]))/g

  // Apply patterns in order
  const wrapOrphan = (match: string) => {
    if (match.includes('__CODE_TOKEN_') || match.includes('__MATH_')) {
      return match
    }
    if (isDev) {
      console.warn('[LaTeX] Wrapped orphan command:', match)
    }
    return `$${match}$`
  }

  text = text.replace(complexOrphanPattern, wrapOrphan)
  text = text.replace(leftRightPattern, wrapOrphan)
  text = text.replace(simpleOrphanPattern, wrapOrphan)
  text = text.replace(operatorPattern, wrapOrphan)

  // 6) Auto-detect standalone mathematical expressions
  // Look for lines that are purely mathematical expressions
  text = text
    .split('\n')
    .map((line) => {
      const trimmed = line.trim()

      // Skip if line is empty, already has math delimiters/placeholders, or is too long
      if (!trimmed || hasMathDelims(trimmed) || line.includes('__MATH_') || trimmed.length > 200)
        return line

      // Skip GFM table rows. Math never renders inside table cells (remarkGfm
      // runs before remarkMath), which is why the table-cleanup passes above
      // strip $-delimiters from rows. Auto-wrapping here would re-inject them —
      // e.g. a connectivity cell `R279032063P01 → R279032064P01` matches the
      // chemical-equation pattern below and becomes `$…$`, leaving a literal
      // dollar-sign artifact in the rendered cell.
      if (trimmed.startsWith('|')) return line

      // Check for inline math expressions in sentences
      // First, handle chemical equations with specific pattern
      // Matches complete chemical equations like "N2 + O2 → N2O5"
      const chemPattern =
        /((?:[A-Z][A-Za-z0-9]*(?:\s*\+\s*[A-Z][A-Za-z0-9]*)*)\s*→\s*(?:[A-Z][A-Za-z0-9]*(?:\s*\+\s*[A-Z][A-Za-z0-9]*)*))(?=\s|$)/g
      line = line.replace(chemPattern, (match, expr) => {
        if (!hasMathDelims(expr) && !expr.includes('__MATH_')) {
          return `$${expr}$`
        }
        return match
      })

      // Then handle math expressions after keywords
      const mathPattern =
        /(?:what is|calculate|solve|find)\s+([^\s,.:;!?]+(?:\s*[+\-*/^]\s*[^\s,.:;!?]+)+)/gi
      line = line.replace(mathPattern, (match, expr) => {
        if (isMathExpression(expr) && !hasMathDelims(expr) && !expr.includes('__MATH_')) {
          return match.replace(expr, `$${expr}$`)
        }
        return match
      })

      // Finally, check if the entire line is a standalone mathematical expression
      // Only if no inline replacements were made

      // CRITICAL: Do NOT wrap lines with markdown syntax - headers, bold, lists, etc.
      // Check for markdown at START of line (headers, lists, blockquotes)
      const hasMarkdownStart = /^#{1,6}\s|^\d+\.\s+\S|^-\s+\S|^\*\s+\S|^>\s/.test(trimmed)
      // Check for markdown bold ANYWHERE in line (not just start/end)
      const hasMarkdownBold = /\*\*[^*]+\*\*/.test(trimmed)
      // Check for markdown italic ANYWHERE in line (single * not preceded/followed by *)
      const hasMarkdownItalic = /(?<!\*)\*[^*]+\*(?!\*)/.test(trimmed)

      if (hasMarkdownStart || hasMarkdownBold || hasMarkdownItalic) {
        return line  // Preserve markdown formatting, don't wrap in math
      }

      // PROSE DETECTION: Don't wrap long sentences as math
      // Skip if line has too many words (likely prose, not pure math)
      const wordCount = trimmed.split(/\s+/).length
      if (wordCount > 10) {
        return line  // Too many words = prose, not math expression
      }

      // Skip if line contains multiple prose indicators (articles, conjunctions)
      const proseWords = ['the', 'a', 'an', 'is', 'are', 'was', 'were', 'this', 'that', 'for', 'with', 'from', 'into', 'therefore', 'however', 'consequently', 'while', 'keeping', 'design', 'targets']
      const proseWordCount = proseWords.filter(word => new RegExp(`\\b${word}\\b`, 'i').test(trimmed)).length
      if (proseWordCount >= 2) {
        return line  // Multiple prose words = not math
      }

      if (
        !line.includes('$') &&
        !line.includes('__MATH_') &&
        isMathExpression(trimmed) &&
        trimmed === line
      ) {
        return `$$${trimmed}$$`
      }

      return line
    })
    .join('\n')

  // 7) Add final catch-all for any remaining LaTeX commands
  // This is a safety net for commands our specific patterns missed
  const catchAllPattern = /(?<![\\$])(\\[a-zA-Z]+(?:\{(?:[^{}]|\{[^}]*\})*\})*)/g

  text = text.replace(catchAllPattern, (match) => {
    if (match.includes('__CODE_TOKEN_') || match.includes('__MATH_')) {
      return match
    }
    // Check exclusion list for non-math LaTeX commands
    const baseCommand = match.match(/^\\[a-zA-Z]+/)?.[0]
    if (baseCommand && NON_MATH_COMMANDS.has(baseCommand)) {
      return match
    }
    // Check if this looks like a file path or other non-math pattern
    if (isLikelyNotMath(match)) {
      return match
    }
    // Check if this looks like a LaTeX command (has backslash + letters)
    if (/^\\[a-zA-Z]+/.test(match)) {
      if (isDev) {
        console.warn('[LaTeX] Final catch-all wrapped:', match)
      }
      return `$${match}$`
    }
    return match
  })

  // 8) Check for any remaining unmatched LaTeX commands (development mode only)
  if (isDev) {
    const remainingLatex = text.match(/\\[a-zA-Z]+/g)
    if (remainingLatex && remainingLatex.length > 0) {
      console.warn('[LaTeX] WARNING: Unmatched LaTeX commands still present:', [...new Set(remainingLatex)])
    }
  }

  // 8.5) Convert LaTeX text subscript/superscript commands (not supported by KaTeX)
  // Note: \textbf, \textit, \texttt, \emph ARE supported by KaTeX - don't convert those!
  // This runs AFTER math block protection to avoid affecting math content
  // Convert directly to Unicode (not HTML) since react-markdown doesn't support raw HTML without rehype-raw

  // Helper: Extract braced content with proper depth counting (handles arbitrarily nested braces)
  // Fixes orphan brace bug: \textsubscript{\textit{\textbf{x}}} needs full depth matching
  const extractBracedContent = (text: string, startIdx: number): string | null => {
    if (text[startIdx] !== '{') return null
    let depth = 0
    let content = ''
    for (let i = startIdx; i < text.length; i++) {
      const char = text[i]
      if (char === '{') {
        depth++
        if (depth > 1) content += char  // Include inner braces in content
      } else if (char === '}') {
        depth--
        if (depth === 0) return content  // Found matching closing brace
        content += char  // Include inner closing braces in content
      } else {
        if (depth > 0) content += char
      }
    }
    return null  // Unclosed braces
  }

  // Helper: Process text/sub/superscript commands with proper brace matching
  const processScriptCommand = (
    text: string,
    command: string,
    commandLen: number,
    charMap: Record<string, string>,
    fallbackPrefix: string
  ): string => {
    let result = ''
    let i = 0
    let matchCount = 0
    while (i < text.length) {
      const idx = text.indexOf(command, i)
      if (idx === -1) {
        result += text.slice(i)
        break
      }
      result += text.slice(i, idx)

      const content = extractBracedContent(text, idx + commandLen)
      if (content === null) {
        // Malformed - leave as-is and advance by full command length (including '{')
        if (isDev) {
          console.log(`  ⚠️  Malformed ${command}: no closing brace at position ${idx}`)
        }
        result += command
        i = idx + command.length  // CRITICAL: Use command.length, not commandLen
        continue
      }

      matchCount++

      // CRITICAL: Check for placeholder tokens to prevent corruption
      // Match any placeholder format: __PREFIX_NUMBER__
      if (/__\w+_\d+__/.test(content)) {
        if (isDev) {
          console.log(`  📦 ${command}{${content}} - skipped (contains placeholder)`)
        }
        result += `${fallbackPrefix}${content}`
        i = idx + commandLen + content.length + 2  // +2 for { and }
        continue
      }

      // CRITICAL: Check for nested LaTeX to prevent garbled output
      if (content.includes('\\')) {
        // Try to strip text formatting commands and convert to Unicode
        const stripped = stripTextFormatting(content)

        if (isDev) {
          console.log(`  🔧 ${command}{${content}} - contains nested LaTeX, stripped to: "${stripped}"`)
        }

        // If stripped version still has backslashes, it has non-formatting LaTeX - keep as-is
        if (stripped.includes('\\')) {
          if (isDev) {
            console.log(`  📝 Still contains LaTeX commands - keeping as-is with fallback`)
          }
          result += `${fallbackPrefix}${content}`
          i = idx + commandLen + content.length + 2
          continue
        }

        // Successfully stripped to plain text - convert to Unicode
        const converted = stripped.split('').map(c => charMap[c] || c).join('')
        const finalResult = converted !== stripped ? converted : `${fallbackPrefix}${stripped}`
        if (isDev) {
          console.log(`  ✅ Converted stripped text: "${stripped}" → "${finalResult}"`)
        }
        result += finalResult
        i = idx + commandLen + content.length + 2
        continue
      }

      // Convert to Unicode (only plain text)
      const converted = content.split('').map(c => charMap[c] || c).join('')
      const finalResult = converted !== content ? converted : `${fallbackPrefix}${content}`
      if (isDev) {
        console.log(`  ✅ ${command}{${content}} → ${finalResult}`)
      }
      result += finalResult
      i = idx + commandLen + content.length + 2
    }
    if (isDev && matchCount > 0) {
      console.log(`  Total ${command} matches: ${matchCount}`)
    }
    return result
  }

  // Process both commands using the helper (handles arbitrarily nested braces)
  // Uses the same character maps as early processing (defined above)
  text = processScriptCommand(text, '\\textsubscript{', 14, SUBSCRIPT_MAP, '_')  // 14 = len('\textsubscript') without '{'
  text = processScriptCommand(text, '\\textsuperscript{', 16, SUPERSCRIPT_MAP, '^')  // 16 = len('\textsuperscript') without '{'

  // 9) Restore math blocks (now containing fixed/nested-free LaTeX)
  text = restoreMath(text)

  // 10) Clean up any upstream escapes around $-delimiters
  text = text.replace(/\\\$\$([\s\S]*?)\\\$\$/g, (_, body) => `$$${body}$$`)
  text = text.replace(/\\\$(.+?)\\\$/g, (_, body) => `$${body}$`)

  // 11) Restore code blocks
  const result = restoreCode(text)

  // DEBUG: Log output for problematic patterns
  if (shouldDebug) {
    console.log('Output (first 300 chars):', result.substring(0, 300))
    console.log('Transformations applied:')
    console.log('  - Input length:', raw.length, '→ Output length:', result.length)
  }

  return result
  } finally {
    if (shouldDebug) {
      console.groupEnd()
    }
  }
}

// ============================================================================
// Function for clipboard - converts LaTeX to plain text
// Added to match colleague's PR for copy-to-clipboard functionality
// ============================================================================

/**
 * Convert LaTeX/KaTeX-rich strings into clean, human-friendly plain text.
 * - Strips math delimiters ($, $$, \( \), \[ \])
 * - Preserves content of formatting commands like \text{...}, \mathrm{...}
 * - Converts common operators to Unicode (e.g., \times → ×, \approx → ≈)
 * - Removes sizing/spacing helpers (\left, \right, \quad, ...)
 * - Flattens simple braced groups and normalizes whitespace
 */
export const latexToPlainText = (input: string): string => {
  if (!input) return ""

  let s = input

  // 2) Display math: strip delimiters only (do not add line breaks)
  s = s.replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, '$1') // \[ ... \]
  s = s.replace(/\$\$\s*([\s\S]*?)\s*\$\$/g, '$1')   // $$ ... $$

  // 3) Inline math: strip delimiters only
  s = s.replace(/\\\(([\s\S]*?)\\\)/g, '$1')   // \( ... \)
  s = s.replace(/\$([^$]+)\$/g, '$1')               // $ ... $

  // 2) Replace common operators/symbols
  const replaceMap: Record<string, string> = {
    // Basic operators
    "\\times": "×",
    "\\cdot": "·",
    "\\cdotp": "·",
    "\\cdots": "⋯",
    "\\ldots": "…",
    "\\vdots": "⋮",
    "\\ddots": "⋱",
    "\\pm": "±",
    "\\mp": "∓",
    "\\div": "÷",
    "\\ast": "*",
    "\\star": "⋆",
    "\\circ": "∘",
    "\\bullet": "•",
    "\\oplus": "⊕",
    "\\ominus": "⊖",
    "\\otimes": "⊗",
    "\\odot": "⊙",

    // Comparison operators
    "\\approx": "≈",
    "\\equiv": "≡",
    "\\neq": "≠",
    "\\ne": "≠",
    "\\leq": "≤",
    "\\le": "≤",
    "\\geq": "≥",
    "\\ge": "≥",
    "\\ll": "≪",
    "\\gg": "≫",
    "\\sim": "∼",
    "\\simeq": "≃",
    "\\cong": "≅",
    "\\propto": "∝",
    "\\parallel": "∥",
    "\\perp": "⊥",

    // Set theory
    "\\subset": "⊂",
    "\\subseteq": "⊆",
    "\\supset": "⊃",
    "\\supseteq": "⊇",
    "\\in": "∈",
    "\\notin": "∉",
    "\\cap": "∩",
    "\\cup": "∪",
    "\\emptyset": "∅",
    "\\varnothing": "∅",

    // Logic
    "\\exists": "∃",
    "\\nexists": "∄",
    "\\forall": "∀",
    "\\neg": "¬",
    "\\lnot": "¬",
    "\\land": "∧",
    "\\lor": "∨",
    "\\wedge": "∧",
    "\\vee": "∨",

    // Arrows
    "\\to": "→",
    "\\rightarrow": "→",
    "\\leftarrow": "←",
    "\\Rightarrow": "⇒",
    "\\Leftarrow": "⇐",
    "\\leftrightarrow": "↔",
    "\\Leftrightarrow": "⇔",
    "\\longrightarrow": "⟶",
    "\\longleftarrow": "⟵",
    "\\uparrow": "↑",
    "\\downarrow": "↓",
    "\\updownarrow": "↕",

    // Greek letters (lowercase)
    "\\alpha": "α",
    "\\beta": "β",
    "\\gamma": "γ",
    "\\delta": "δ",
    "\\epsilon": "ε",
    "\\zeta": "ζ",
    "\\eta": "η",
    "\\theta": "θ",
    "\\iota": "ι",
    "\\kappa": "κ",
    "\\lambda": "λ",
    "\\mu": "μ",
    "\\nu": "ν",
    "\\xi": "ξ",
    "\\omicron": "ο",
    "\\pi": "π",
    "\\rho": "ρ",
    "\\sigma": "σ",
    "\\tau": "τ",
    "\\upsilon": "υ",
    "\\phi": "φ",
    "\\chi": "χ",
    "\\psi": "ψ",
    "\\omega": "ω",

    // Greek letters (uppercase)
    "\\Gamma": "Γ",
    "\\Delta": "Δ",
    "\\Theta": "Θ",
    "\\Lambda": "Λ",
    "\\Xi": "Ξ",
    "\\Pi": "Π",
    "\\Sigma": "Σ",
    "\\Upsilon": "Υ",
    "\\Phi": "Φ",
    "\\Psi": "Ψ",
    "\\Omega": "Ω",

    // Special symbols
    "\\infty": "∞",
    "\\partial": "∂",
    "\\nabla": "∇",
    "\\degree": "°",
    "\\angle": "∠",
    "\\triangle": "△",
    "\\square": "□",
    "\\diamond": "⋄",
    "\\ell": "ℓ",
    "\\hbar": "ℏ",
    "\\aleph": "ℵ",
    "\\wp": "℘",
    "\\Re": "ℜ",
    "\\Im": "ℑ",
    "\\top": "⊤",
    "\\bot": "⊥",

    // Spacing helpers
    "\\quad": "  ",
    "\\qquad": "    ",
  }

  s = s.replace(/\\[a-zA-Z]+\*?/g, (m) => {
    if (m === "\\left" || m === "\\right") return ""
    return replaceMap[m] ?? m // keep unknown commands as their bare name later
  })

  // Handle punctuation-based spacing commands separately (not matched by [a-zA-Z]+)
  s = s.replace(/\\,/g, " ")
  s = s.replace(/\\;/g, " ")
  s = s.replace(/\\:/g, " ")
  s = s.replace(/\\!/g, "")

  // 3) Unwrap formatting commands with a single braced argument: \text{...} -> ...
  // Repeat a few times to handle simple nesting
  for (let i = 0; i < 5; i++) {
    const before = s
    s = s.replace(/\\[a-zA-Z]+\*?\s*\{([^{}]*)\}/g, "$1")
    if (s === before) break
  }

  // 4) Remove leftover backslashes from unknown commands (e.g., \\alpha -> alpha)
  s = s.replace(/\\([a-zA-Z]+)\*?/g, "$1")

  // 5) Drop any remaining grouping braces
  s = s.replace(/[{}]/g, "")

  // 6) Tidy punctuation spacing (keep original newlines and other spacing)
  // Remove spaces immediately before common punctuation
  s = s.replace(/\s+([.,;:!?])(\s|$)/g, '$1$2')

  return s
}
